// ─────────────────────────────────────────────────────────────────────────────
// FtueAnchorDirective
// ─────────────────────────────────────────────────────────────────────────────
// Declares "this element is the semantic anchor `<key>`", so a tip can point at
// it without any component knowing a CSS selector.
//
//   <span [appFtueAnchor]="ftueAnchors.ORDER_COMPANY"> … </span>
//
// Registration is what makes a tip presentable: <ui-ftue-layer> waits for the
// key to appear and abandons the tip (leaving it eligible) when it never does.
// Anchors must sit on a header, a toolbar or a pinned element — never on a
// virtualized ag-Grid row, which can be recycled under the open card.
// ─────────────────────────────────────────────────────────────────────────────

import { Directive, ElementRef, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

import { FtueAnchorRegistryService } from '../core/ftue/ftue-anchor-registry.service';
import { FtueAnchorKey } from '../core/ftue/ftue-anchors';

@Directive({
  selector: '[appFtueAnchor]',
  standalone: true,
})
export class FtueAnchorDirective implements OnChanges, OnDestroy {

  /**
   * Optional so a list can bind it per row (`[appFtueAnchor]="field.ftueAnchor"`)
   * without every row becoming an anchor.
   */
  @Input('appFtueAnchor') key?: FtueAnchorKey;

  /**
   * Mirrors the key into the DOM. Not used for lookup — the registry holds the
   * element itself — but it makes anchors visible in devtools and in the
   * inspector, which is where anyone debugging a tip starts.
   */
  @HostBinding('attr.data-ftue')
  get dataFtue(): string | null {
    return this.key ?? null;
  }

  private registeredKey?: FtueAnchorKey;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly anchors: FtueAnchorRegistryService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['key']) {
      return;
    }
    this.unregister();
    if (this.key) {
      this.anchors.register(this.key, this.elementRef.nativeElement);
      this.registeredKey = this.key;
    }
  }

  ngOnDestroy(): void {
    this.unregister();
  }

  private unregister(): void {
    if (this.registeredKey) {
      this.anchors.unregister(this.registeredKey, this.elementRef.nativeElement);
      this.registeredKey = undefined;
    }
  }
}
